import React, { useState, useEffect } from 'react'
import { Modal, Upload, Icon, Breadcrumb, message } from 'antd'
import { useTranslation } from 'react-i18next'
import UploadAdModalHolder from '../../styles/uploadAdModalStyle'
import BreadcrumbEllipsis from '../BreadcrumbEllipsis'
import { findFolderPath, collapsePath } from '../../utils/folderTree'
import { translateTagName } from '../../../campaignManager/hooks/contentManager/useLibraryContents'
import { ACCEPT, isAllowedType, isAllowedSize } from '../../utils/fileValidation'

const { Dragger } = Upload

// Icon shown next to each selected file by kind.
const fileIcon = (file) => {
  const type = (file && file.type) || ''
  if (type.startsWith('image/')) return 'picture'
  if (type.startsWith('video/')) return 'video-camera'
  if (type.startsWith('audio/')) return 'sound'
  return 'file'
}

const formatSize = (bytes) => {
  if (bytes == null) return ''
  const mb = bytes / 1024 / 1024
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

const sameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified

const UploadAdModal = ({ visible, folders = [], currentTag = null, uploading = false, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const [files, setFiles] = useState([])

  // Files are uploaded to the folder the user is currently standing in
  // (currentTag); the breadcrumb just shows where that is. Empty path = root.
  const path = currentTag != null ? findFolderPath(folders, currentTag) : []

  useEffect(() => {
    if (visible) setFiles([])
  }, [visible])

  // Validate each file (type/size) and append it. Returning false stops the
  // Dragger from auto-uploading — we keep the selection local and upload on OK.
  // Multiple files can be added across several drops/picks; duplicates are skipped.
  const beforeUpload = (file) => {
    if (!isAllowedType(file) || !isAllowedSize(file)) {
      message.error(t('Supports JPG, PNG, MP4, MP3 (Max 500MB)'))
      return false
    }
    setFiles((prev) => (prev.some((f) => sameFile(f, file)) ? prev : [...prev, file]))
    return false
  }

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index))

  const handleOk = () => {
    if (!files.length) return
    // Filed under the current folder (currentTag), resolved by the container's
    // uploadAds wrapper. Each file keeps its own name (handled server-side).
    onSubmit(files, {})
  }

  return (
    <Modal
      title={t('Upload new ad')}
      visible={visible}
      okText={files.length > 1 ? t('Upload {{count}} files', { count: files.length }) : t('Upload file')}
      cancelText={t('Cancel')}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={uploading}
      okButtonProps={{ disabled: !files.length }}
      destroyOnClose
    >
      <UploadAdModalHolder>
        {/* Indicates the folder the files will be uploaded to (read-only). */}
        <Breadcrumb className="upload-breadcrumb" separator=">">
          <Breadcrumb.Item>{t('My ads')}</Breadcrumb.Item>
          {collapsePath(path).map((item) =>
            item.ellipsis ? (
              // Informational only: hover shows the hidden folders, no navigation.
              <Breadcrumb.Item key="ellipsis">
                <BreadcrumbEllipsis hidden={item.hidden} className="upload-bc-ellipsis" />
              </Breadcrumb.Item>
            ) : (
              <Breadcrumb.Item key={item.folder.id}>
                {translateTagName(item.folder.name, t)}
              </Breadcrumb.Item>
            ),
          )}
        </Breadcrumb>

        <Dragger multiple accept={ACCEPT} showUploadList={false} beforeUpload={beforeUpload}>
          <div className="dragger-content">
            <p className="ant-upload-drag-icon">
              <Icon type="inbox" />
            </p>
            <p>{t('Click to upload or drag your files here')}</p>
            <p className="upload-hint">{t('Supports JPG, PNG, MP4, MP3 (Max 500MB)')}</p>
          </div>
        </Dragger>

        {/* Selected files: visible + removable. The user can keep adding more. */}
        {files.length > 0 && (
          <div className="selected-files">
            <div className="selected-files-head">
              {t('{{count}} files selected', { count: files.length })}
            </div>
            <ul className="selected-files-list">
              {files.map((file, i) => (
                <li key={`${file.name}-${file.size}-${i}`} className="selected-file">
                  <Icon type={fileIcon(file)} className="file-icon" />
                  <span className="file-name" title={file.name}>
                    {file.name}
                  </span>
                  <span className="file-size">{formatSize(file.size)}</span>
                  <Icon
                    type="close"
                    className="file-remove"
                    title={t('Remove')}
                    onClick={() => removeFile(i)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </UploadAdModalHolder>
    </Modal>
  )
}

export default UploadAdModal
